import { Button, Card, Image, makeStyles, Text } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import caseStudyService from "../../../services/CaseStudyService";
import { useTranslation } from "react-i18next";
import IPagePortfolio, { IPortfolioItem } from "../../../models/page/pageComponents/IPagePortfolio";
import TitleText from "../../../components/titleText/TitleText";
import ICaseStudyPreview from "../../../models/caseStudy/ICaseStudyPreview";
import { Link } from "react-router-dom";

const useStyles = makeStyles({
    mainContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "2rem",
        margin: "0 auto",
        maxWidth: "1200px",
        flexDirection: "column",
    },
    cardContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        width: "100%",
        minHeight: "500px",
        maxHeight: "650px",
        overflow: "hidden",
        flexDirection: "column"
    },
    cardWrapper: {
        position: "absolute",
        width: "300px",
        height: "calc(100% - 50px)",
        minHeight: "400px",
        maxHeight: "600px",
        transition: "transform 0.5s ease-in-out, opacity 0.5s ease-in-out",
    },
    shadowCard: {
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        borderRadius: "8px",
        overflow: "hidden",
        padding: "20px",
        textAlign: "center",
        backgroundColor: "white",
        height: "100%",
    },
    cardImage: {
        display: "flex",
        width: "100%",
        height: "auto",
        maxHeight: "175px",
        objectPosition: "center",
        objectFit: "cover",
        padding: 0,
        top: 0,
        left: 0,
    },
    cardTitle: {
        display: "flex",
        fontSize: "1.25rem",
        fontWeight: 600,
        marginTop: "auto",
        marginBottom: "10px",
    },
    cardClient: {
        display: "flex",
        fontSize: "0.875rem",
        fontWeight: 500,
        color: "#888",
        marginBottom: "6px",
    },
    cardDescription: {
        display: "flex",
        fontSize: "1rem",
        marginBottom: "15px",
        color: "#555",
    },
    cardLink: {
        textDecoration: "none",
        color: "inherit",
        display: "block",
        width: "100%",
        textAlign: "center",
    },
    cardButton: {
        display: "flex",
        marginTop: "15px",
    },
    navigation: {
        marginTop: "4rem",
        gap: "1rem",
        display: "flex",
    },
});

const PortfolioItem = (props: IPortfolioItem) => {
    const { t } = useTranslation("general");
    const styles = useStyles();

    const getCardStyle = (index: number) => {
        const relativeIndex = (index - props.currentIndex + props.caseStudiesLength) % props.caseStudiesLength;

        // Left card
        if (relativeIndex === 0) {
            return { transform: "translateX(-350px)", opacity: 0.5, zIndex: 1 };
        }
        
        // Center (focused) card
        if (relativeIndex === 1) {
            return { transform: "translateX(0)", opacity: 1, zIndex: 2 };
        }
        
        // Right card
        if (relativeIndex === 2) {
            return { transform: "translateX(350px)", opacity: 0.5, zIndex: 1 };
        }

        // Hidden cards (to handle edge cases)
        return { transform: "translateX(1000px)", opacity: 0, zIndex: 0 };
    };

    return (
        <div className={styles.cardWrapper} style={getCardStyle(props.caseStudyIndex)}>
            <Card className={styles.shadowCard}>
                <Image
                    src={props.caseStudy.thumbnail.url}
                    alt={props.caseStudy.thumbnail.altText}
                    className={styles.cardImage}
                />
                <Text className={styles.cardTitle} size={400} weight="semibold">
                    {props.caseStudy.title}
                </Text>
                <Text className={styles.cardClient} size={200}>
                    {props.caseStudy.client.name}
                </Text>
                <Text className={styles.cardDescription} size={300}>
                    {props.caseStudy.subtitle}
                </Text>
                <Link to={`/casestudy/${props.caseStudy.slug}`} className={styles.cardLink}>
                    <Button appearance="secondary" className={styles.cardButton}>
                        {t("readMore")}
                    </Button>
                </Link>
            </Card>
        </div>
    );
};

export default function ServicePortfolio(props: IPagePortfolio) {
    const { t } = useTranslation("general");
    const styles = useStyles();

    const [caseStudies, setCaseStudies] = useState<ICaseStudyPreview[]>([]);
    const [currentIndex, setCurrentIndex] = useState(1);
    
    useEffect(() => {
        fetchCaseStudies(props.caseStudyIds)
            .then((caseStudies) => setCaseStudies(caseStudies));
    }, []);

    const handlePrev = () => {
        setCurrentIndex((index) => (index === 0 ? caseStudies.length - 1 : index - 1));
    };

    const handleNext = () => {
        setCurrentIndex((index) => (index === caseStudies.length - 1 ? 0 : index + 1));
    };

    return (
        <div className={styles.mainContainer}>
            <TitleText
                title={props.title}
                titleHighlightedWords={props.titleHighlightedWords}
                subtitle={props.subtitle}
                alignment={"center"}
                width="100%"
                />

            <div className={styles.cardContainer}>
                {
                    caseStudies.map((caseStudy, index) => (
                        <PortfolioItem
                            key={index}
                            caseStudy={caseStudy}
                            caseStudyIndex={index}
                            currentIndex={currentIndex}
                            caseStudiesLength={caseStudies.length}
                            />
                    ))
                }
            </div>

            <div className={styles.navigation}>
                <Button onClick={handlePrev}>
                    {t("previous")}
                </Button>
                <Button onClick={handleNext}>
                    {t("next")}
                </Button>
            </div>
        </div>
    );
}

async function fetchCaseStudies(caseStudyIds: string[]) : Promise<ICaseStudyPreview[]> {
    const caseStudies = await Promise.all(
        caseStudyIds.map(async (id) => await caseStudyService.getCaseStudyPreview(id))
    );

    return caseStudies;
}